import React, { useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useUserAccessData } from './hooks/useUserAccessData';
import UserAccessTable from './components/UserAccessTable';
import UserAccessDialog from './components/UserAccessDialog';
import '../authorization-v2.scss';

const apiErrorMessage = (error, fallback) => error?.data?.message || error?.data?.errors?.join?.(', ') || error?.message || fallback;

const UserAccess = () => {
    const { th } = useLanguage();
    const toast = useRef(null);
    const [dialog, setDialog] = useState({ visible: false, user: null });
    const data = useUserAccessData();

    const closeDialog = () => setDialog({ visible: false, user: null });

    const handleSubmit = async ({ isEditMode, userId, ...payload }) => {
        try {
            if (isEditMode) {
                await data.saveRoles({ userId, userType: payload.userType, roleIds: payload.roleIds });
                toast.current?.show({ severity: 'success', summary: th('common.success'), detail: th('authorizationV2.user.rolesSaved'), life: 3000 });
            } else {
                await data.createUser(payload);
                toast.current?.show({ severity: 'success', summary: th('common.success'), detail: th('authorizationV2.user.created'), life: 3000 });
            }
            closeDialog();
        } catch (error) {
            const detail = error?.accountCreated ? th('authorizationV2.user.createdWithoutRoles') : apiErrorMessage(error, th('authorizationV2.user.saveError'));
            toast.current?.show({ severity: error?.accountCreated ? 'warn' : 'error', summary: th(error?.accountCreated ? 'common.warning' : 'common.error'), detail, life: 8000 });
            if (error?.accountCreated) {
                closeDialog();
                data.refetch();
            }
        }
    };

    return (
        <div className="card authz-v2">
            <Toast ref={toast} />
            <div className="flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="m-0">{th('authorizationV2.user.title')}</h1>
                    <small className="text-color-secondary">{th('authorizationV2.user.subtitle')}</small>
                </div>
                <Button type="button" label={th('authorizationV2.user.newUser')} icon="pi pi-user-plus" onClick={() => setDialog({ visible: true, user: null })} />
            </div>

            {data.error && <Message severity="error" className="w-full mb-3" text={apiErrorMessage(data.error, th('authorizationV2.user.loadError'))} />}

            <UserAccessTable
                users={data.users}
                totalCount={data.totalCount}
                loading={data.loading}
                lazyParams={data.lazyParams}
                onPage={data.onPage}
                onSort={data.onSort}
                onSearch={data.applyFilters}
                onReset={data.resetFilters}
                onEdit={(user) => setDialog({ visible: true, user })}
            />

            <UserAccessDialog visible={dialog.visible} user={dialog.user} saving={data.saving} onHide={closeDialog} onSubmit={handleSubmit} />
        </div>
    );
};

export default UserAccess;
